import React, { useRef, useState, useEffect } from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup, Typography,
  TextField,
  Box,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Create as CreateIcon,
  DeleteOutline,
  Close,
  ArrowBackOutlined,
  ArrowForwardOutlined,
  CheckCircleOutlineOutlined,
  WarningOutlined,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MGenericButton from '~/components/PF2MGenericButton';
import {
  Card, CardBody, CardHeader, CardIcon,
} from '~/components/Card';
import { setHighlightEquipmentStatus, updateEquipmentStatus } from '~/store/dispatch/actions';
import { Field, Form, Formik } from 'formik';
import PF2MDatePickerOutlined from '~/components/PF2MDatePickerOutlined';
import moment from 'moment';
import {
  newMomentDate,
  parseDateTimeToTimestamp,
  verifyTime,
  parseDate,
  subtractDaysFromNow,
} from '~/utils/moment';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';
import ReactInputMask from 'react-input-mask';
import PF2MAutocomplete from '~/components/PF2MAutocomplete';

const SelectMultiple = ({
  options,
  multiple,
  disabled,
  onChange,
  value,
  selectAllValue,
  classes,
}) => {
  const selectedOptions = options.filter(opt => (value || []).includes(opt.value));

  return (
    <>
      <Box>
        <PF2MAutocomplete
          title={''}
          multiple={multiple}
          disabled={disabled}
          options={options}
          onChange={onChange}
          value={selectedOptions}
          selectAllValue={selectAllValue}
          limitTags={1}
          disablePortal
          classes={{
            paper: classes.autocompletePaperMargin,
          }}
          className={classes.boxDescription}
        />
      </Box>
    </>
  );
};

const FormControlDefaults = ({
  formLabel,
  children,
  classes,
  isRequired,
}) => (
  <FormControl className={classes.modelParent} required={isRequired}>
    <FormLabel className={classes.formLabel}>
      {formLabel}
    </FormLabel>
    <FormGroup>
      {children}
    </FormGroup>
  </FormControl>
);

FormControlDefaults.propTypes = {
  classes: PropTypes.object.isRequired,
  formLabel: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
  isRequired: PropTypes.bool,
};

FormControlDefaults.defaultProps = {
  isRequired: false,
};

const EquipmentStatusEditAllModal = ({
  classes, handleClose,
}) => {
  // os campos iniciais precisam ser carregados junto com o componente.
  const INIT_SELECTED_FIELDS = {
    id_code_group: 0,
    code_type: -1,
    id_code: 0,
    equip_type_id: 0,
    id_equipament: [],
    equip_id: [],
    start_date: newMomentDate(),
    start_time: moment(new Date()).format('HH:mm:ss'),
  };

  const [isModalOpen, setModalOpen] = useState(false);
  const [formikErrors, setFormikErrors] = useState({});
  const [selectedFields, setSelectedFields] = useState(INIT_SELECTED_FIELDS);
  const [hasEmptyRequiredFields, setHasEmptyRequiredFields] = useState(true);
  const [isConfirmChangesStep, setIsConfirmChangesStep] = useState(false);
  const formikRef = useRef();

  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const codes = useSelector(state => state.nref.codes);
  const codeTypes = useSelector(state => state.nref.codeTypes.map(e => ({ ...e, name: translate(`common:${e.name}`) })));
  const codeGroups = useSelector(state => state.nref.codeGroups);
  const equipmentTypes = useSelector(state => state.nref.equipmentTypes);
  const equipmentGroups = useSelector(state => state.nref.equipmentGroups);
  const equipments = useSelector(state => state.nref.equipments);
  const equipmentStatuses = useSelector(state => state.dispatch.equipmentStatuses);

  const closures = useSelector(state => state.manager.closures);
  const userAccessLevel = useSelector(state => state.auth.user.level);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const filterCodeGroups = u => u.id_equip === selectedFields.equip_type_id
    && (selectedFields.code_type === -1 || u.code_type === selectedFields.code_type);

  const filterCodes = c => c.id_equip === selectedFields.equip_type_id
    && (selectedFields.code_type === -1 || c.code_type === selectedFields.code_type)
    && (selectedFields.id_code_group === 0 || c.id_group === selectedFields.id_code_group);

  const filterEquipmentGroups = e => e.id_equipament === selectedFields.equip_type_id;

  const filterEquipments = e => e.id_equip === selectedFields.equip_type_id
    && (!selectedFields.id_equipament?.length
      || selectedFields.id_equipament.includes(e.id_group));

  const handleUpdateItems = () => {
    const selectedEquipmentStatuses = equipmentStatuses.filter(
      e => selectedFields.equip_id.includes(e.id_equip),
    );

    const timestampToDate = timestamp => moment.unix(timestamp / 1000).format('DD/MM/YYYY');

    const newSelectedItems = selectedEquipmentStatuses.map((i) => {
      const startDate = (
        selectedFields.start_date
          ? timestampToDate(selectedFields.start_date)
          : i.start_date
      );

      const startTime = (
        selectedFields.start_time
          ? selectedFields.start_time
          : i.start_time
      );

      const startTimestamp = parseDateTimeToTimestamp(startDate, startTime) / 1000;

      return {
        ...i,
        start_date: startDate,
        start_time: startTime,
        start_timestamp: startTimestamp,
        code_type: selectedFields.code_type !== -1 ? selectedFields.code_type : i.code_type,
        id_code_group: selectedFields.id_code_group !== -1
          ? selectedFields.id_code_group : i.id_code_group,
        id_code: selectedFields.id_code ? selectedFields.id_code : i.id_code,
      };
    });

    const ids = newSelectedItems.map(item => item?.id_equip);
    return dispatch(updateEquipmentStatus({
      equipmentStatus: newSelectedItems,
      finallyCb: () => dispatch(setHighlightEquipmentStatus(ids)),
    }));
  };

  const requiredFields = [
    'equip_type_id',
    'id_equipament',
    'equip_id',
    'code_type',
    'id_code_group',
    'id_code',
  ];

  // Função para verificar se todos os campos obrigatórios estão preenchidos
  const checkAllRequiredFieldsFilled = () => {
    const hasInvalidField = requiredFields.some((field) => {
      const value = selectedFields[field];

      if (field === 'id_equipament' || field === 'equip_id') {
        return !value || value.length === 0;
      }

      if (field === 'code_type') {
        return value === -1;
      }

      if (field === 'id_code') {
        return value === 0;
      }

      return value === 0 || value === -1;
    });

    if (hasInvalidField) {
      return false;
    }

    const startDate = selectedFields.start_date;
    const startTime = selectedFields.start_time;

    if (!startDate || !moment(startDate).isValid()) {
      return false;
    }

    if (!startTime || !verifyTime(startTime)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    const allRequiredFieldsFilled = checkAllRequiredFieldsFilled();
    setHasEmptyRequiredFields(!allRequiredFieldsFilled);
  }, [selectedFields]);

  const modalHandleChange = (field, value) => {
    setSelectedFields((prevSelectedFields) => {
      let newFields;
      switch (field) {
        case 'code_type':
          newFields = {
            ...prevSelectedFields,
            id_code_group: 0,
            id_code: 0,
            code_type: value,
          };
          break;
        case 'id_code_group':
          newFields = {
            ...prevSelectedFields,
            id_code: 0,
            id_code_group: value,
          };
          break;
        default:
          newFields = { ...prevSelectedFields, [field]: value };
      }

      return newFields;
    });
  };

  const handleSave = () => {
    handleUpdateItems();
    handleClose();
  };

  const handleDiscardChanges = () => {
    setSelectedFields(INIT_SELECTED_FIELDS);
    setFormikErrors({});
    setHasEmptyRequiredFields(true);
    if (formikRef.current) {
      formikRef.current.resetForm(INIT_SELECTED_FIELDS);
    }
  };

  const handleContinue = () => {
    if (formikRef.current) {
      formikRef.current.validateForm().then((errors) => {
        if (Object.keys(errors).length > 0 || hasEmptyRequiredFields) {
          setFormikErrors(errors);
          setModalOpen(true);
          return;
        }
        setIsConfirmChangesStep(true);
      });
    }
  };

  const handleBack = () => {
    setIsConfirmChangesStep(false);
  };

  const renderHeader = () => (
    <div>
      <Typography
        variant="h4"
        align="center"
        gutterBottom
      >
        {`${translate('common:EditRecordsInBatches')}`}
      </Typography>
    </div>
  );

  const renderSelectField = ({
    fieldName,
    formLabel,
    collection,
    errors,
    touched = {},
  }) => {
    const defaultSelectValues = [
      {
        value: INIT_SELECTED_FIELDS[fieldName],
        label: translate('common:Select'),
      },
    ];

    const normalizedCollection = defaultSelectValues.concat(
      ...collection
        .map(e => ({ value: e.id, label: e.name }))
        .sort((a, b) => {
          const labelA = String(a?.label ?? '').trim();
          const labelB = String(b?.label ?? '').trim();

          return labelA.localeCompare(labelB, undefined, {
            sensitivity: 'base',
            numeric: true,
            ignorePunctuation: true,
          });
        }),
    );

    const fieldValue = selectedFields[fieldName];

    let isFieldEmpty;
    if (fieldName === 'code_type') {
      isFieldEmpty = fieldValue === -1;
    } else if (fieldName === 'id_code') {
      isFieldEmpty = fieldValue === 0;
    } else {
      isFieldEmpty = fieldValue === 0 || fieldValue === -1;
    }

    const fieldWasTouched = touched[fieldName];
    const shouldShowError = isFieldEmpty && fieldWasTouched;

    const hasError = shouldShowError || !!errors[fieldName];
    const errorMessage = shouldShowError
      ? translate('validation:RequiredField')
      : errors[fieldName];

    return (
      <FormControlDefaults formLabel={formLabel} classes={classes} isRequired>
        <Field
          name={fieldName}
          render={({ field, form }) => (
            <PF2MSearchSelectOutlined
              {...field}
              placeholder={null}
              onChange={(e) => {
                if (!touched[fieldName]) {
                  form.setFieldTouched(fieldName, true);
                }

                form.setFieldValue(fieldName, e.target.value);
                modalHandleChange(fieldName, e.target.value);
              }}
              onBlur={() => form.setFieldTouched(fieldName, true)}
              className={hasError ? classes.fieldError : classes.field}
              error={hasError}
              helperText={errorMessage}
            >
              {normalizedCollection}
            </PF2MSearchSelectOutlined>
          )}
        />
      </FormControlDefaults>
    );
  };

  const renderMultiSelectField = ({
    fieldName,
    formLabel,
    collection,
    errors = {},
  }) => {
    const normalizedCollection = collection && Array.isArray(collection)
      ? collection
        .map(e => ({
          value: e.id,
          label: e.name,
        }))
        .sort((a, b) => {
          const labelA = String(a?.label ?? '').trim();
          const labelB = String(b?.label ?? '').trim();

          return labelA.localeCompare(labelB, undefined, {
            sensitivity: 'base',
            numeric: true,
            ignorePunctuation: true,
          });
        })
      : [];

    return (
      <FormControlDefaults formLabel={formLabel} classes={classes} isRequired>
        <Field
          name={fieldName}
          render={({ field, form }) => {
            const { value } = field;
            const isFieldTouched = form.touched && form.touched[fieldName];
            const fieldError = errors[fieldName];

            const shouldShowError = isFieldTouched && !!fieldError;
            const hasError = shouldShowError || !!fieldError;

            return (
              <div className={`${classes.field} ${hasError ? classes.fieldError : ''}`}>
                <SelectMultiple
                  {...field}
                  description=""
                  multiple
                  disabled={false}
                  classes={classes}
                  options={normalizedCollection}
                  value={value}
                  onChange={(_, val) => {
                    const values = val.map(v => v.value);

                    if (!form.touched[fieldName]) {
                      form.setFieldTouched(fieldName, true);
                    }

                    form.setFieldValue(fieldName, values);
                    modalHandleChange(fieldName, values);
                  }}
                  onBlur={() => {
                    if (!form.touched[fieldName]) {
                      form.setFieldTouched(fieldName, true);
                    }
                    field.onBlur();
                  }}
                />
              </div>
            );
          }}
        />
      </FormControlDefaults>
    );
  };

  const renderDateField = ({ fieldName, formLabel, errors }) => {
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);
    const isEnableClosureOn = enableClosure && closures?.length > 0;

    if (isEnableClosureOn) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig : lockEditLastClosure.add(1, 'day');
    }

    const fieldValue = selectedFields[fieldName];
    const isFieldEmpty = !fieldValue;
    const hasError = isFieldEmpty || !!errors[fieldName];
    const errorMessage = errors[fieldName] || (isFieldEmpty ? translate('validation:RequiredField') : '');

    return (
      <FormControlDefaults formLabel={formLabel} classes={classes} isRequired>
        <Field
          name={fieldName}
          render={({ field, form }) => (
            <>
              <PF2MDatePickerOutlined
                field={field}
                form={form}
                minDate={userLimitEdit}
                onChange={(e) => {
                  form.setFieldValue(fieldName, e, true);
                  modalHandleChange(fieldName, e);
                }}
                error={hasError}
                helperText={errorMessage}
              />
            </>
          )}
        />
      </FormControlDefaults>
    );
  };

  const renderTimeField = ({ fieldName, formLabel, errors }) => {
    const fieldValue = selectedFields[fieldName];
    const isFieldEmpty = !fieldValue || fieldValue === '00:00:00';
    const hasError = isFieldEmpty || !!errors[fieldName];
    const errorMessage = errors[fieldName] || (isFieldEmpty ? translate('validation:RequiredField') : '');

    return (
      <FormControlDefaults formLabel={formLabel} classes={classes} isRequired>
        <Field
          name={fieldName}
          render={({ field, form }) => (
            <ReactInputMask
              mask="99:99:99"
              value={field.value}
              onChange={(e) => {
                field.onChange(e);
                form.setFieldValue(fieldName, e.target.value);
                modalHandleChange(fieldName, e.target.value);
              }}
            >
              {inputProps => (
                <TextField
                  {...inputProps}
                  variant="outlined"
                  error={hasError}
                  helperText={errorMessage}
                  style={{ height: '28px' }}
                  InputProps={{
                    style: { fontSize: 13 },
                    classes: { input: classes.inputMask },
                  }}
                />
              )}
            </ReactInputMask>
          )}
        />
      </FormControlDefaults>
    );
  };

  const validateAllFields = (values) => {
    const errors = {};

    requiredFields.forEach((field) => {
      const value = values[field];

      if (field === 'id_equipament' || field === 'equip_id') {
        if (!value || value.length === 0) {
          errors[field] = translate('validation:RequiredField');
        }
      } else if (field === 'id_code') {
        if (value === 0) {
          errors[field] = translate('validation:RequiredField');
        }
      } else if (field === 'code_type') {
        if (value === -1) {
          errors[field] = translate('validation:RequiredField');
        }
      } else if (value === 0 || value === -1) {
        errors[field] = translate('validation:RequiredField');
      }
    });

    // Validação da data
    const startDate = typeof values.start_date === 'number' ? moment.unix(values.start_date) : values.start_date;
    if (!startDate || !startDate.isValid()) {
      errors.start_date = translate('validation:RequiredField');
    }

    // Validação do horário
    if (!verifyTime(values.start_time)) {
      errors.start_time = translate('validation:InvalidHour');
    } else {
      const currentMoment = newMomentDate();
      const dateTimeString = `${startDate.format('DD/MM/YYYY')} ${values.start_time}`;
      const dateTimeMoment = moment(dateTimeString, 'DD/MM/YYYY HH:mm:ss', true);

      if (dateTimeMoment.isAfter(currentMoment)) {
        errors.start_time = translate('validation:FutureTimeCantBeSelected');
      }

      if (dateTimeMoment.startOf('day').isAfter(currentMoment.startOf('day'))) {
        errors.start_date = translate('validation:FutureDateCantBeSelected');
      }
    }

    setFormikErrors(errors);
    return errors;
  };

  const renderFormControl = () => (
    <Formik
      ref={formikRef}
      initialValues={selectedFields}
      validateOnChange
      validateOnBlur
      validate={validateAllFields}
    >
      {({ values, errors }) => (
        <Form className={classes.formContainer}>
          {renderSelectField({
            fieldName: 'equip_type_id',
            formLabel: translate('common:EquipmentType'),
            collection: equipmentTypes,
            errors,
          })}
          {renderMultiSelectField({
            fieldName: 'id_equipament',
            formLabel: translate('common:EquipmentGroup'),
            collection: equipmentGroups.filter(filterEquipmentGroups),
            errors,
          })}
          {renderMultiSelectField({
            fieldName: 'equip_id',
            formLabel: translate('common:Equipment'),
            collection: equipments.filter(filterEquipments),
            errors,
          })}

          {renderSelectField({
            fieldName: 'code_type',
            formLabel: translate('common:CodeType'),
            collection: codeTypes,
            errors,
          })}
          {renderSelectField({
            fieldName: 'id_code_group',
            formLabel: translate('common:CodeGroup'),
            collection: codeGroups.filter(filterCodeGroups),
            errors,
          })}
          {renderSelectField({
            fieldName: 'id_code',
            formLabel: translate('common:Code'),
            collection: codes.filter(filterCodes),
            errors,
          })}

          {renderDateField({
            fieldName: 'start_date',
            formLabel: translate('common:Date'),
            errors,
          })}
          {renderTimeField({
            values,
            errors,
            fieldName: 'start_time',
            formLabel: translate('common:Time'),
          })}
          <div />
        </Form>
      )}
    </Formik>
  );

  const selectedCode = codes.find(c => (
    c.id === selectedFields?.id_code
    && c.id_equip === selectedFields?.equip_type_id
  ));

  const isInvisibleCode = [0, 3].includes(selectedCode?.is_visible)
  || [3, 4].includes(selectedCode?.code_type);

  const renderConfirmChanges = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
        padding: '0 32px 32px 32px',
      }}
    >
      {isInvisibleCode ? (
        <>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 16,
            }}
          >
            <WarningOutlined style={{ fontSize: 46, color: '#FFC107' }} />
            <Typography style={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: 30,
            }}
            >
              {translate('common:AttentionAlert')}
            </Typography>
          </div>
          <div
            style={{
              textAlign: 'start',
              gap: 20,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography style={{ fontSize: 24 }}>
              {translate('common:InvisibleCodeAlert')}
            </Typography>
            <Typography style={{ fontSize: 24, fontWeight: 'bold' }}>
              {translate('common:OnlyVisibleOnPlatform')}
            </Typography>
            <Typography style={{ fontSize: 24 }}>
              {translate('common:ToSendTurnOnVisible')}
            </Typography>
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 20,
            }}
          >
            <WarningOutlined style={{ fontSize: 46, color: '#FFC107' }} />
            <Typography style={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: 30,
            }}
            >
              {translate('common:AttentionAlert')}
            </Typography>
          </div>
          <div style={{
            textAlign: 'start', gap: 20, display: 'flex', flexDirection: 'column',
          }}
          >
            <Typography style={{ fontSize: 24 }}>
              {translate('common:ChangeWillBeSent')}
            </Typography>
            <Typography style={{ fontSize: 24, fontWeight: 'bold' }}>
              {translate('common:OperatorWillReceiveMessageAlert')}
            </Typography>
            <Typography style={{ fontSize: 24 }}>
              {translate('common:DoYouWantToContinue')}
            </Typography>
          </div>
        </>
      )}
    </div>
  );

  const renderConfirmButtons = () => (
    <div className={classes.wrapperButtons}>
      <PF2MGenericButton
        color="primary"
        onClick={handleBack}
        startIcon={<ArrowBackOutlined fontSize="small" />}
      >
        {translate('common:Back')}
      </PF2MGenericButton>
      <PF2MGenericButton
        variant="contained"
        color="secondary"
        onClick={handleSave}
        disabled={hasEmptyRequiredFields || Object.keys(formikErrors).length > 0}
        startIcon={<CheckCircleOutlineOutlined fontSize="small" />}
      >
        {translate('common:Commit Changes')}
      </PF2MGenericButton>
    </div>
  );

  const renderFormButtons = () => (
    <div className={classes.wrapperButtons}>
      <PF2MGenericButton
        color="primary"
        onClick={handleDiscardChanges}
        startIcon={<DeleteOutline fontSize="small" />}
      >
        {translate('common:DiscardChanges')}
      </PF2MGenericButton>
      <PF2MGenericButton
        variant="contained"
        color="secondary"
        onClick={handleContinue}
        disabled={hasEmptyRequiredFields || Object.keys(formikErrors).length > 0}
        startIcon={<ArrowForwardOutlined fontSize="small" />}
      >
        {translate('common:Continue')}
      </PF2MGenericButton>
    </div>
  );

  return (
    <>
      <Card style={{ maxWidth: 840 }}>
        <CardHeader icon>
          <CardIcon color="warning" className={classes.cardIconWarning}>
            <CreateIcon className={classes.headerIcons} />
          </CardIcon>
          <CardIcon
            onClick={() => handleClose()}
            style={{
              float: 'right',
              cursor: 'pointer',
              padding: '13px',
              background: 'darkgray',
              borderRadius: 50,
              boxShadow: '10px 10px 50px gray',
              marginRight: '0px',
            }}
          >
            <Close className={classes.headerIcons} />
          </CardIcon>
        </CardHeader>
        <CardBody>
          {renderHeader()}
          <hr />
          {isConfirmChangesStep ? renderConfirmChanges() : renderFormControl()}
          <hr />
          <div className={classes.buttonContainer}>
            {isConfirmChangesStep ? renderConfirmButtons() : renderFormButtons()}
          </div>
        </CardBody>
      </Card>
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:ThereAreBlankFields')}
        open={isModalOpen}
        onConfirm={() => setModalOpen(false)}
      />
    </>
  );
};

EquipmentStatusEditAllModal.propTypes = {
  classes: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
};

EquipmentStatusEditAllModal.defaultProps = {};

export default withStyles(styles)(EquipmentStatusEditAllModal);
